import { useEffect, useId, useState } from 'react';

import { Button, Divider } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { completarEndereco, consultarCep, digitosDoCep } from '@/comuns/cep';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { EdicaoDeMembro, MembroDaEquipe } from '@/data/tipos';
import { atual } from '@/session/sessao';
import { gerarSenha, SENHA_MINIMA } from './NovoUsuarioDaEquipeModal';

const PAPEIS: { value: string; label: string }[] = [
    { value: 'profissional', label: 'Profissional' },
    { value: 'recepcao', label: 'Recepção' },
    { value: 'financeiro', label: 'Financeiro' },
];

/** O "—" é a lista dizendo "vazio"; no campo, vazio é vazio. */
const campo = (v: string | undefined) => (v && v !== '—' ? v : '');

function formDe(m: MembroDaEquipe) {
    return {
        nome: m.nome, papel: m.papel, telefone: campo(m.telefone),
        especialidades: campo(m.especialidades), registro: campo(m.registro), senha: '',
        cep: campo(m.cep), estado: campo(m.estado), cidade: campo(m.cidade), bairro: campo(m.bairro),
        rua: campo(m.rua), numero: campo(m.numero), complemento: campo(m.complemento),
    };
}

/** As partes do endereço que mudaram — vazio apaga a coluna, como o telefone. */
const ENDERECO = ['cep', 'estado', 'cidade', 'bairro', 'rua', 'numero', 'complemento'] as const;

export interface EditarUsuarioDaEquipeModalProps {
    membro: MembroDaEquipe | null;
    onClose: () => void;
    onSalvo: () => void;
}

/**
 * Edita quem já está na equipe e, se preciso, define uma nova senha.
 *
 * O e-mail não se edita: é ele que amarra o login ao membro (0007), e trocá-lo
 * aqui deixaria a conta de um lado e o cadastro do outro. O que o banco recusa
 * (o próprio papel, a senha da proprietária) a tela nem oferece, mas a recusa
 * continua sendo do banco e da borda.
 */
export function EditarUsuarioDaEquipeModal({ membro, onClose, onSalvo }: EditarUsuarioDaEquipeModalProps) {
    const aviso = useAviso();
    const ids = {
        nome: useId(), email: useId(), papel: useId(), telefone: useId(), funcao: useId(), registro: useId(), senha: useId(),
        cep: useId(), estado: useId(), cidade: useId(), bairro: useId(), rua: useId(), numero: useId(), complemento: useId(),
    };

    const [form, setForm] = useState(() => (membro ? formDe(membro) : null));
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setForm(membro ? formDe(membro) : null);
        setErro(null);
    }, [membro]);

    if (!membro || !form) return null;

    const original = formDe(membro);
    const mudar = (m: Partial<typeof original>) => { setForm((f) => (f ? { ...f, ...m } : f)); setErro(null); };
    const sujo = JSON.stringify(form) !== JSON.stringify(original);

    const souEu = !!membro.email && membro.email.toLowerCase() === atual()?.email?.toLowerCase();
    const proprietaria = membro.papel === 'proprietaria';
    const papelFixo = souEu || proprietaria;
    const aceitaSenha = membro.acesso === 'ativo' && !souEu && !proprietaria;
    const opcoesDePapel = PAPEIS.some((p) => p.value === membro.papel)
        ? PAPEIS
        : [{ value: membro.papel, label: proprietaria ? 'Proprietária' : membro.papel }, ...PAPEIS];

    const salvar = async (): Promise<boolean> => {
        if (!form.nome.trim()) { setErro('O nome não pode ficar vazio.'); return false; }
        if (form.senha && form.senha.length < SENHA_MINIMA) {
            setErro(`A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`);
            return false;
        }

        const mudancas: EdicaoDeMembro = {};
        if (form.nome.trim() !== original.nome) mudancas.nome = form.nome.trim();
        if (form.papel !== original.papel) mudancas.papel = form.papel;
        if (form.telefone !== original.telefone) mudancas.telefone = form.telefone;
        if (form.especialidades !== original.especialidades) mudancas.especialidades = form.especialidades;
        if (form.registro !== original.registro) mudancas.registro = form.registro;
        for (const parte of ENDERECO) {
            if (form[parte] !== original[parte]) mudancas[parte] = form[parte];
        }

        setSalvando(true);
        try {
            if (Object.keys(mudancas).length > 0) await dados().atualizarMembro(membro.id, mudancas);
            /* A senha vem depois dos dados: se ela for recusada, o que já foi
               salvo fica, e o erro diz só o que faltou. */
            if (form.senha) {
                try {
                    await dados().definirSenhaDoMembro(membro.id, form.senha);
                } catch (e) {
                    if (Object.keys(mudancas).length > 0) onSalvo();
                    setErro(`Dados salvos, mas a senha não foi definida: ${e instanceof Error ? e.message : 'tente de novo.'}`);
                    return false;
                }
            }
            aviso({
                title: form.senha ? 'Usuário atualizado e senha definida' : 'Usuário atualizado',
                description: form.senha ? `${form.nome.trim()} já entra com a nova senha.` : form.nome.trim(),
            });
            onSalvo();
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={520} title={`Editar ${membro.nome}`}
            description="O cadastro de contato da pessoa e, se precisar, uma nova senha."
            dirty={sujo} onSave={salvar}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!sujo}>Salvar</Button>
                </>
            }
        >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {erro && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <Alert tone="danger" dismissible={false}>{erro}</Alert>
                    </div>
                )}
                <FormField label="Nome completo" required htmlFor={ids.nome} style={{ gridColumn: '1 / -1' }}>
                    <Input id={ids.nome} value={form.nome} onChange={(v) => mudar({ nome: v })} />
                </FormField>
                {/* Mostrar sem editar: o e-mail é a chave entre a conta e o
                    cadastro (0007), e a guarda da 0037 recusa a troca pela API.
                    `readOnly` em vez de `disabled` para continuar copiável. */}
                <FormField
                    label="E-mail de acesso" htmlFor={ids.email} style={{ gridColumn: '1 / -1' }}
                    hint={membro.email
                        ? 'É com ele que a pessoa entra. Não muda por aqui.'
                        : 'Sem e-mail, esta pessoa ainda não entra: cadastre-a de novo em Novo usuário.'}
                >
                    <Input id={ids.email} type="email" value={membro.email ?? ''} readOnly placeholder="—" />
                </FormField>
                <FormField
                    label="Papel" htmlFor={ids.papel}
                    hint={souEu ? 'Ninguém muda o próprio papel.' : proprietaria ? 'A proprietária é definida pela plataforma.' : undefined}
                >
                    <Select id={ids.papel} value={form.papel} onChange={(v) => mudar({ papel: v })} options={opcoesDePapel} disabled={papelFixo} />
                </FormField>
                <FormField label="Telefone" htmlFor={ids.telefone}>
                    <Input id={ids.telefone} type="tel" value={form.telefone} onChange={(v) => mudar({ telefone: v })} placeholder="(11) 99999-9999" />
                </FormField>
                <FormField label="Função" htmlFor={ids.funcao}>
                    <Input id={ids.funcao} value={form.especialidades} onChange={(v) => mudar({ especialidades: v })} placeholder="Dermatologista" />
                </FormField>
                <FormField label="Registro no conselho" htmlFor={ids.registro}>
                    <Input id={ids.registro} value={form.registro} onChange={(v) => mudar({ registro: v })} placeholder="CRM-SP 000.000" />
                </FormField>
                <Divider label="Endereço" spacing={4} style={{ gridColumn: '1 / -1' }} />
                <FormField label="CEP" htmlFor={ids.cep} hint="Ao sair do campo, o endereço vazio é completado pelo CEP.">
                    <Input
                        id={ids.cep} value={form.cep} onChange={(v) => mudar({ cep: v })} placeholder="00000-000"
                        onBlur={() => {
                            if (!digitosDoCep(form.cep)) return;
                            void consultarCep(form.cep).then((r) => {
                                /* `completarEndereco` só preenche o que está vazio:
                                   o que a pessoa digitou continua dela. */
                                if (r.ok) setForm((f) => (f ? completarEndereco(f, r.endereco) : f));
                                else aviso({ title: 'Endereço não completado', description: r.motivo });
                            });
                        }}
                    />
                </FormField>
                <FormField label="Estado" htmlFor={ids.estado}>
                    <Input id={ids.estado} value={form.estado} onChange={(v) => mudar({ estado: v })} placeholder="SP" />
                </FormField>
                <FormField label="Cidade" htmlFor={ids.cidade}>
                    <Input id={ids.cidade} value={form.cidade} onChange={(v) => mudar({ cidade: v })} />
                </FormField>
                <FormField label="Bairro" htmlFor={ids.bairro}>
                    <Input id={ids.bairro} value={form.bairro} onChange={(v) => mudar({ bairro: v })} />
                </FormField>
                <FormField label="Rua" htmlFor={ids.rua}>
                    <Input id={ids.rua} value={form.rua} onChange={(v) => mudar({ rua: v })} />
                </FormField>
                <FormField label="Número" htmlFor={ids.numero}>
                    <Input id={ids.numero} value={form.numero} onChange={(v) => mudar({ numero: v })} />
                </FormField>
                <FormField label="Complemento" htmlFor={ids.complemento} style={{ gridColumn: '1 / -1' }}>
                    <Input id={ids.complemento} value={form.complemento} onChange={(v) => mudar({ complemento: v })} placeholder="Apartamento, bloco, sala" />
                </FormField>
                {aceitaSenha && <Divider label="Acesso" spacing={4} style={{ gridColumn: '1 / -1' }} />}
                {aceitaSenha && (
                    <FormField
                        label="Nova senha" htmlFor={ids.senha} style={{ gridColumn: '1 / -1' }}
                        hint={`Deixe em branco para manter a atual. Pelo menos ${SENHA_MINIMA} caracteres; entregue-a à pessoa, nenhum e-mail é enviado.`}
                    >
                        <div style={{ display: 'flex', gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Input id={ids.senha} value={form.senha} onChange={(v) => mudar({ senha: v })} placeholder="Nova senha" autoComplete="new-password" />
                            </div>
                            <Button variant="secondary" onClick={() => mudar({ senha: gerarSenha() })}>Gerar</Button>
                        </div>
                    </FormField>
                )}
            </div>
        </Modal>
    );
}
